import { Campaign, ChannelType, LocaleOption, Template } from '../../../types'
import FormWrapper from '../../../ui/form/FormWrapper'
import Modal from '../../../ui/Modal'
import SwitchField from '../../../ui/form/SwitchField'
import { useContext, useEffect, useState } from 'react'
import api from '../../../api'
import { AdminContext, ProjectContext } from '../../../contexts'
import { SingleSelect } from '../../../ui/form/SingleSelect'
import { LinkButton } from '../../../ui'
import { useTranslation } from 'react-i18next'
import { checkOrganizationRole } from '../../../utils'
import { localeOption } from './TemplateContextProvider'
import { UseFormReturn } from 'react-hook-form'
import { emailChannel } from './email'
import { textChannel } from './text'
import { inAppChannel } from './in-app'

interface CreateTemplateParams {
    open: boolean
    setIsOpen: (state: boolean) => void
    campaign: Campaign
    onCreate: (campaign: Campaign, locale: LocaleOption) => void
}

export interface LocaleParams {
    locale: string
    ai_translate?: boolean
    data: Record<string, any>
}

export interface CreateOptionsProps {
    form: UseFormReturn<LocaleParams, any>
    campaign: Campaign
}

export interface CreateTemplateContext {
    campaign: Campaign
    data: Record<string, any>
    clonedTemplate: Template | undefined
    initialData: any
}

export type CreateTemplateFn = (ctx: CreateTemplateContext) => Promise<{ data: any, postCreate?: (template: Template) => Promise<Template> }>

export interface ChannelConfig {
    CreateOptions?: React.FC<CreateOptionsProps>
    createTemplate?: CreateTemplateFn
}

const channelConfig: Partial<Record<ChannelType, ChannelConfig>> = {
    email: emailChannel,
    text: textChannel,
    in_app: inAppChannel,
}

export default function TemplateCreateModal({ open, setIsOpen, campaign, onCreate }: CreateTemplateParams) {

    const { t } = useTranslation()
    const [project] = useContext(ProjectContext)
    const admin = useContext(AdminContext)
    const [locales, setLocales] = useState<LocaleOption[]>([])
    const [hasAI, setHasAI] = useState(false)
    const [archivedLocales, setArchivedLocales] = useState<string[]>([])
    useEffect(() => {
        api.locales.search(project.id, { limit: 100 })
            .then((result) => setLocales(result.results))
            .catch(() => {})
        api.providers.all(project.id)
            .then((providers) => setHasAI(providers.some(p => p.group === 'ai')))
            .catch(() => {})
    }, [])

    useEffect(() => {
        if (!open) return
        api.campaigns.templates(campaign.project_id, campaign.id, true)
            .then((templates) => setArchivedLocales(templates.filter(template => template.deleted_at).map(template => template.locale)))
            .catch(() => {})
    }, [open, campaign.id, campaign.project_id])

    async function handleCreateTemplate({ locale, ai_translate, data }: LocaleParams) {
        const clonedTemplate = campaign.templates.find(template => template.locale === 'en') ?? campaign.templates[0]
        let initialData = clonedTemplate?.data || data ? { ...clonedTemplate?.data, ...data } : undefined

        const config = channelConfig[campaign.channel]
        let postCreate: ((template: Template) => Promise<Template>) | undefined
        if (config?.createTemplate) {
            const result = await config.createTemplate({ campaign, data, clonedTemplate, initialData })
            initialData = result.data
            postCreate = result.postCreate
        }

        let template = await api.templates.create(campaign.project_id, {
            campaign_id: campaign.id,
            type: campaign.channel,
            locale,
            ai_translate,
            data: initialData,
        })

        if (postCreate) {
            template = await postCreate(template)
        }

        const newCampaign = { ...campaign }
        newCampaign.templates.push(template)
        onCreate(newCampaign, localeOption(locale))
        setIsOpen(false)
    }

    const existingLocales = new Set([...campaign.templates.map(tmpl => tmpl.locale), ...archivedLocales])
    const availableLocales = locales.filter(l => !existingLocales.has(l.key))
    const hasExistingTemplates = campaign.templates.length > 0
    const existingEditor = campaign.templates[0]?.data?.editor

    const ChannelCreateOptions = channelConfig[campaign.channel]?.CreateOptions

    return (
        <Modal title={t('create_template')}
            open={open}
            onClose={() => setIsOpen(false)}
            zIndex={1000}>
            <FormWrapper<LocaleParams>
                key={availableLocales[0]?.key}
                defaultValues={{ locale: availableLocales[0]?.key, ...(existingEditor ? { data: { editor: existingEditor } } : {}) }}
                onSubmit={async (params) => { await handleCreateTemplate(params) }}
                submitLabel={t('create')}>
                {form => <>
                    <p>{t('create_template_description')}</p>
                    <SingleSelect.Field
                        form={form}
                        name="locale"
                        label={t('language_locale')}
                        options={availableLocales}
                        toValue={option => option.key}
                        required
                        suffix={
                            checkOrganizationRole('admin', admin?.role) && (
                                <LinkButton
                                    variant="secondary"
                                    to={`/projects/${project.id}/settings/locales`}>{t('create_locale')}</LinkButton>
                            )
                        }/>

                    {hasAI && hasExistingTemplates && (
                        <SwitchField
                            form={form}
                            name="ai_translate"
                            label={t('ai_translate')}
                            subtitle={t('ai_translate_description')}
                        />
                    )}
                    {ChannelCreateOptions && (
                        <ChannelCreateOptions
                            form={form}
                            campaign={campaign}
                        />
                    )}
                </>}
            </FormWrapper>
        </Modal>
    )
}
